/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from "react"
import { UseControllerProps, useController } from "react-hook-form"

import {
  appendSuffixToStringNumber,
  transformNumberToStringWithSuffix,
  transformStringWithSuffixToNumber,
} from "../../utils/transformNumber"
import { inputCss } from "../OrdersTable.styled"
import { FlexContainerFullHeight } from "./Components.styled"
import { ErrorIconWithTooltip } from "../../../../components"
import { WaveInputs } from "../types/form"
import { Icon, Tooltip } from "@blueprintjs/core"

/**
 * Specific DollarAmountInput which is able to:
 *      _ As a user I want to see 10K for 10 000, 12M for 12 000 000.
 *      _ As a user if I write 10 then automatically I want see 10K after leaving cell.
 *      _ As a user if I want million, I have to add M after number. So if I want 12 000 000, I can write 12M
 *      _ As a dev, I want to register in the payload the extended number (10 000 for 10K)
 *      _ As a dev, the system must be able from the payload to display compact number.
 *
 * It takes numeric value, transform it into string with suffix K or M and display it.
 * When user leave the cell, the string is transform into a numeric value.
 */

interface INumericInputWithSuffixProps extends UseControllerProps<WaveInputs> {
  latestPriceUsd?: number
  latestPrice?: number
  latestPriceLatency?: string
  index: number
  onChange?: () => void
}

export const DollarAmountInput = ({
  index,
  control,
  name,
  rules,
  onChange,
  latestPriceUsd,
  latestPrice,
  latestPriceLatency,
}: INumericInputWithSuffixProps) => {
  const { field, fieldState } = useController({ control, name, rules })

  // Track previous value to detect external changes
  const prevValueRef = useRef(field.value)

  // Local display state
  const [displayValue, setDisplayValue] = useState<string>(
    field.value ? transformNumberToStringWithSuffix(field.value) : "",
  )

  // Update display when field value changes externally
  useEffect(() => {
    if (prevValueRef.current !== field.value) {
      setDisplayValue(field.value ? transformNumberToStringWithSuffix(Number(field.value)) : "")
    }
    prevValueRef.current = field.value
  }, [field.value])

  // Handle user input
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value.trim()
    if (inputValue === "" || /^\d+\.?(\d+)?[mMkK]?$/.test(inputValue)) {
      setDisplayValue(inputValue.toUpperCase())
    }
  }

  // Finalize value on blur
  const handleBlur = () => {
    const formatted = appendSuffixToStringNumber(displayValue, "K")
    setDisplayValue(formatted)

    const numericValue = transformStringWithSuffixToNumber(formatted)
    field.onChange(numericValue)
    if (onChange) onChange()
  }

  return (
    <FlexContainerFullHeight alignItems="center" position="relative">
      <input
        type="text"
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        css={inputCss(index)}
      />
      {fieldState.error && <ErrorIconWithTooltip errorMessage={fieldState.error.message} />}
      {!fieldState.error && latestPriceUsd && latestPriceLatency && (
        <Tooltip
          content={
            <div>
              <div>{`${Number(latestPriceUsd).toFixed(2)} $ (${latestPriceLatency})`}</div>
              {latestPrice && <div>{`${Number(latestPrice).toFixed(2)} (local)`}</div>}
            </div>
          }
          placement="bottom"
          openOnTargetFocus={false}
          compact
        >
          <Icon
            icon="dollar"
            size={18}
            style={{
              color: latestPriceLatency === "live" ? undefined : "#FF7A00",
            }}
          />
        </Tooltip>
      )}
    </FlexContainerFullHeight>
  )
}
